import { Button, Flex } from 'antd';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@shared/ui/antd';
import { useGetIntegrations } from '@modules/integrations/hooks/use-get-integrations';
import { useDeleteIntegration } from '@modules/integrations/hooks/use-delete-integration';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { ScrollContainer } from '@shared/ui/scroll-container';
import { Trash } from '@shared/assets/icons/components';
import styles from './integrations-section.module.scss';

interface ChannelIntegrationsSectionProps {
  channelId?: number;
  onCreateIntegration?: () => void;
  onEditIntegration?: (integrationId: number) => void;
}

export const ChannelIntegrationsSection: FC<ChannelIntegrationsSectionProps> = ({
  channelId,
  onCreateIntegration,
  onEditIntegration,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'integrations' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common.actions' });
  const normalizedChannelId = channelId ?? 0;
  const { integrations, isLoading } = useGetIntegrations(normalizedChannelId);
  const { openConfirmModal } = useGlobalConfirmModalActions();
  const { deleteIntegration, loadingIntegrationId } = useDeleteIntegration();
  const isChannelReady = Boolean(channelId);

  const handleCreate = () => {
    if (!isChannelReady) return;

    onCreateIntegration?.();
  };

  const hasIntegrations = isChannelReady && (integrations?.length ?? 0) > 0;

  const handleOpenDeleteConfirmModal = (integrationId: number, webhookTitle: string) => {
    openConfirmModal({
      title: t('deleteModal.title'),
      text: t('deleteModal.text', {
        webhook: webhookTitle,
      }),
      confirmButtonLabel: tCommon('delete'),
      onConfirm: async () => {
        await deleteIntegration(integrationId);
      },
    });
  };

  return (
    <Flex vertical className={styles.root} gap={12}>
      <Text variant="md" fontWeight={500}>
        {t('title')}
      </Text>
      <Text variant="sm" className={styles.description}>
        {t('description')}
      </Text>

      {!hasIntegrations && (
        <Button
          type="default"
          className={styles.addButton}
          onClick={handleCreate}
          disabled={isLoading || !isChannelReady}
        >
          {t('add')}
        </Button>
      )}

      {hasIntegrations && (
        <Flex vertical gap={12}>
          <ScrollContainer maxHeight="700px" height="auto" style={{ flex: '0 1 auto' }}>
            <Flex vertical gap={12}>
              {integrations!.map((integration) => (
                <Flex
                  key={integration.id}
                  className={styles.item}
                  justify="space-between"
                  align="center"
                >
                  <Flex vertical gap={4}>
                    <Text variant="md" fontWeight={400}>
                      {integration.title || t('emptyWebhookTitle')}
                    </Text>
                    <Text variant="sm" className={styles.status}>
                      {t('activeStatus')}
                    </Text>
                  </Flex>
                  <Flex align="center" className={styles.itemActions}>
                    <Button
                      type="default"
                      className={styles.editButton}
                      onClick={() => onEditIntegration?.(integration.id)}
                      disabled={loadingIntegrationId === integration.id}
                    >
                      {t('edit')}
                    </Button>
                    <Button
                      type="default"
                      className={styles.deleteButton}
                      icon={<Trash />}
                      loading={loadingIntegrationId === integration.id}
                      disabled={
                        loadingIntegrationId !== null && loadingIntegrationId !== integration.id
                      }
                      onClick={() =>
                        handleOpenDeleteConfirmModal(
                          integration.id,
                          integration.title || t('emptyWebhookTitle'),
                        )
                      }
                      aria-label={tCommon('delete')}
                    />
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </ScrollContainer>

          <Button
            type="default"
            className={styles.addButton}
            onClick={handleCreate}
            disabled={isLoading || !isChannelReady}
          >
            {t('add')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
